import type { ReactElement, ReactNode } from 'react';
import type {
    AdvertBannerGridTile,
    AdvertPotmDesktopGridTile,
    AdvertPotmMobileGridTile,
    AdvertProjectPlacementGridTile,
    DirectoryPotmDesktopGridTile,
    GridTile,
    ProjectAdvertGridTile,
    ProjectTile,
} from './buildProjectTileGrid';
import {
    useAppDisplayModeContext,
    Caption,
    HotButtons,
    useStyles,
} from '@archipro-design/aria';
import { getDirectoryTileImageProps } from '~/modules/root/util/get-directory-tile-image-props';
import type { GetTileHotButtonsProps } from '~/modules/root/hook/use-tile-hot-buttons';
import type { DirectoryItemAdmin } from '~/modules/directory/type';
import { logDirectoryItemClick } from '~/modules/category/util/log-directory-item-click';
import { useTracker } from '@archipro-website/tracker';
import ProjectAdvertTile from '@modules/tile/component/project-tile/ProjectAdvertTile';
import { getHotButtons } from '~/modules/directory/util/get-hot-buttons';
import type { ProjectTileProps } from '~/modules/projects-category/util/project-tile-view-model.server';
import { AdvertProjectPlacementTile } from '~/modules/adverts/component/advert-project-placement/AdvertProjectPlacementTile';
import DirectoryPotmTileDesktop from '@modules/projects-category/components/directory-grid-potm/DirectoryPotmTileDesktop';
import AdvertBannerTile from '@modules/adverts/component/advert-banner-tile/AdvertBannerTile';
import APConnectBanner from '~/modules/category/component/ap-connect-banner/APConnectBanner';
import * as S from '~/modules/projects-category/components/project-category-container/ProjectCategoryContainer.style';
import AdvertPotmTileDesktop from '~/modules/projects-category/components/directory-grid-potm/AdvertPotmTileDesktop';
import AdvertPotmTileMobile from '~/modules/projects-category/components/directory-grid-potm/AdvertPotmTileMobile';
import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';

interface RenderProps {
    mobile: boolean;
    getTileTopActions: <T>(
        props: GetTileHotButtonsProps<T>,
    ) => JSX.Element | undefined;
    willDelete: (itemIDToDelete: number) => void;
    getTileAdminCaption: (
        adminItem: DirectoryItemAdmin | number,
    ) => JSX.Element | undefined;
    filterDrawerOpen: boolean;
    desktop: boolean;
    showViewCount: boolean;
    enableAwardBadges: boolean;
    canEditAdmin: boolean;
    featuredProjectIds?: number[];
    unfeaturedIds?: Set<number>;
    newlyFeaturedIds?: Set<number>;
    removedFromLibraryIds?: Set<number>;
    onToggleFeature?: (projectId: number, feature: boolean) => void;
    onRemoveFromLibrary?: (
        itemType: 'project' | 'product',
        itemId: number,
        itemName: string,
    ) => void;
}

interface TileProps extends RenderProps {
    tileData: GridTile;
}

interface RenderProjectTileGridProps extends RenderProps {
    tiles: GridTile[];
}

type RenderProjectTileGridSignature = (
    props: RenderProjectTileGridProps,
) => ReactNode;

const Tile = ({
    tileData: { component, data },
    mobile,
    getTileAdminCaption,
    filterDrawerOpen,
    desktop,
    canEditAdmin,
    featuredProjectIds,
    unfeaturedIds,
    newlyFeaturedIds,
    removedFromLibraryIds,
    onToggleFeature,
    onRemoveFromLibrary,
}: TileProps) => {
    const gridColumns = desktop ? (filterDrawerOpen ? 3 : 4) : 1;

    const tracker = useTracker();
    const appDisplayMode = useAppDisplayModeContext();
    const { css } = useStyles();

    if (component === 'ProjectTile') {
        const { project } = data as ProjectTile['data'];
        return (
            <CategoryPageTileWithAdminActions
                key={project.model.id}
                tile={project}
                featuredProjectIds={featuredProjectIds}
                unfeaturedIds={unfeaturedIds}
                newlyFeaturedIds={newlyFeaturedIds}
                removedFromLibraryIds={removedFromLibraryIds}
                onToggleFeature={onToggleFeature}
                onRemoveFromLibrary={onRemoveFromLibrary}
            />
        );
    } else if (component === 'ProjectAdvertTile') {
        const { index, project } = data as ProjectAdvertGridTile['data'];
        const { tile } = project;

        const imageProps = getDirectoryTileImageProps(
            index,
            1,
            3,
            mobile,
            tile.image,
            tile.projectName,
        );

        const hotButtons = getHotButtons<ProjectTileProps>({
            item: tile,
            itemType: 'project',
            appDisplayMode,
            canEditAdmin,
            canEdit: Boolean(tile.canEdit),
            editTooltip:
                'Ad components cannot be edited. Please make changes directly on the project tile',
            onEditClick: () => {},
        });

        const adminCaption = getTileAdminCaption(tile.projectID);

        return (
            <ProjectAdvertTile
                tag={'Sponsored'}
                advertCreative={project.advertCreative}
                key={`sponsored_${tile.projectLink}`}
                prefetch="intent"
                image={imageProps}
                projectName={{
                    children: mobile ? (
                        <Caption variant="04" weight="medium">
                            {tile.projectName}
                        </Caption>
                    ) : (
                        tile.projectName
                    ),
                }}
                professionalName={{
                    children: mobile ? (
                        <Caption variant="04">{tile.professionalName}</Caption>
                    ) : (
                        tile.professionalName
                    ),
                }}
                variant={desktop ? '01' : '02'}
                size={
                    !desktop
                        ? 'dynamic'
                        : !filterDrawerOpen
                          ? 'large'
                          : 'medium'
                }
                variables={{
                    ...(!desktop && {
                        dynamicSizingWidth: 414,
                        dynamicSizingGapWidth: 0,
                        tilePadding: 0,
                    }),
                }}
                onClick={() => {
                    logDirectoryItemClick(tracker, {
                        itemID: project.projectId,
                        offset: index,
                        categoryType: 'projects',
                        appDisplayMode,
                    });
                }}
                impression={{
                    professionalID: project.tile.professionalID,
                    itemID: project.projectId,
                    type: 'LibraryItem_Directory',
                }}
                topActions={
                    hotButtons ? <HotButtons items={hotButtons} /> : undefined
                }
                adminCaption={adminCaption}
                newDesignEnabled={true}
            />
        );
    } else if (component === 'AdvertProjectPlacementTile') {
        const { ad } = data as AdvertProjectPlacementGridTile['data'];
        return <AdvertProjectPlacementTile ad={ad} columns={gridColumns} />;
    } else if (component === 'DirectoryPotmTileDesktop') {
        const { potmData } = data as DirectoryPotmDesktopGridTile['data'];
        return <DirectoryPotmTileDesktop {...potmData} />;
    } else if (component === 'AdvertBannerTile') {
        const { advertCreative } = data as AdvertBannerGridTile['data'];
        return (
            <AdvertBannerTile
                advertCreative={advertCreative}
                desktop={desktop}
                columns={gridColumns}
            />
        );
    } else if (component === 'APConnectBanner') {
        return (
            <div className={css(S.APConnectBannerCell)}>
                <APConnectBanner />
            </div>
        );
    } else if (component === 'AdvertPotmTileDesktop') {
        const { advertCreative } = data as AdvertPotmDesktopGridTile['data'];
        return <AdvertPotmTileDesktop advertCreative={advertCreative} />;
    } else if (component === 'AdvertPotmTileMobile') {
        const { advertCreative } = data as AdvertPotmMobileGridTile['data'];
        return <AdvertPotmTileMobile advertCreative={advertCreative} />;
    } else {
        return <></>;
    }
};

export const renderProjectTileGrid: RenderProjectTileGridSignature = ({
    tiles,
    ...props
}): ReactElement[] => {
    return tiles.map((tile) => (
        <Tile tileData={tile} key={tile.key} {...props} />
    ));
};
